import React, { useState } from 'react';

import { useSnackbar } from 'notistack';

import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';

import {
  useGetTourOfferDepartureItineraries,
  useSaveTourOfferDepartureItinerary,
} from '~/hooks/apis/tours/useTourOffers';

import { TourOfferDepartureItinerary, TourOfferItineraryInput } from '~/types/tours/tourOffer';

import NativeTourCard from './NativeTourCard';
import NativeTourItineraryEditor from './NativeTourItineraryEditor';
import { isItineraryInvalid, itineraryDaysFrom } from './nativeTourForm';

interface Props {
  tourOfferId: string;
  configurationId: string;
  /** The option's itinerary as it stands in the editor, which departures are copied from. */
  optionItinerary: TourOfferItineraryInput;
  hosts: Array<{ svcImageId: string; name: string }>;
}

function itineraryOf(departure: TourOfferDepartureItinerary): TourOfferItineraryInput {
  return { days: itineraryDaysFrom(departure.days), transfer: departure.transfer };
}

/** Departures with an itinerary of their own, which replaces the option's for that departure. */
export default function NativeTourDepartureItineraries({
  tourOfferId,
  configurationId,
  optionItinerary,
  hosts,
}: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const { data: departures = [], isLoading, error } = useGetTourOfferDepartureItineraries(tourOfferId, configurationId);
  const save = useSaveTourOfferDepartureItinerary(tourOfferId, configurationId);
  const [selected, setSelected] = useState<Array<string>>([]);
  const [editing, setEditing] = useState<{ departureId: string; itinerary: TourOfferItineraryInput } | null>(null);

  const editingDeparture = departures.find((departure) => departure.departureId === editing?.departureId);

  const saveOne = async (departureId: string, itinerary: TourOfferItineraryInput) =>
    await save.mutateAsync({ departureId, itinerary });

  const copyToSelected = async () => {
    const failed: Array<string> = [];
    for (const departureId of selected) {
      try {
        await saveOne(departureId, optionItinerary);
      } catch (copyError) {
        const departure = departures.find((candidate) => candidate.departureId === departureId);
        failed.push(`${departure?.startDate ?? departureId}: ${copyError.message}`);
      }
    }
    setSelected([]);
    if (failed.length > 0) {
      enqueueSnackbar(`Some departures weren't copied. ${failed.join('; ')}`, { variant: 'error' });
    } else {
      enqueueSnackbar('Itinerary copied to the selected departures', { variant: 'success' });
    }
  };

  if (isLoading) {
    return (
      <Box p={2} display="flex" justifyContent="center">
        <CircularProgress size={24} />
      </Box>
    );
  }
  if (error) {
    return <Alert severity="error">{error.message}</Alert>;
  }

  return (
    <NativeTourCard
      title="Itineraries by departure"
      description="Give a departure an itinerary of its own, e.g. a different hotel. Saved straight away, apart from the rest of the page."
    >
      {departures.length === 0 ? (
        <Typography color="text.secondary">This option has no departures.</Typography>
      ) : (
        <Stack spacing={2}>
          <List dense disablePadding sx={{ maxHeight: 360, overflowY: 'auto' }}>
            {departures.map((departure) => {
              const hasOwn = departure.days.length > 0;
              return (
                <ListItem
                  key={departure.departureId}
                  disablePadding
                  secondaryAction={
                    hasOwn ? (
                      <Button
                        size="small"
                        onClick={() =>
                          setEditing({ departureId: departure.departureId, itinerary: itineraryOf(departure) })
                        }
                      >
                        Edit
                      </Button>
                    ) : undefined
                  }
                >
                  <ListItemButton
                    dense
                    onClick={() =>
                      setSelected((current) =>
                        current.includes(departure.departureId)
                          ? current.filter((id) => id !== departure.departureId)
                          : [...current, departure.departureId],
                      )
                    }
                  >
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <Checkbox
                        edge="start"
                        size="small"
                        checked={selected.includes(departure.departureId)}
                        tabIndex={-1}
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary={`${departure.startDate}${departure.name ? ` · ${departure.name}` : ''}`}
                      secondary={`${departure.lengthInDays} days`}
                    />
                    <Chip
                      size="small"
                      variant="outlined"
                      color={hasOwn ? 'primary' : 'default'}
                      label={hasOwn ? 'Own itinerary' : "Follows the option's"}
                      sx={{ mr: hasOwn ? 6 : 0 }}
                    />
                  </ListItemButton>
                </ListItem>
              );
            })}
          </List>
          <Box>
            <Button
              variant="contained"
              disabled={selected.length === 0 || save.isPending || isItineraryInvalid(optionItinerary, null)}
              onClick={copyToSelected}
            >
              Copy the option's itinerary to {selected.length || 'the selected'} departure
              {selected.length === 1 ? '' : 's'}
            </Button>
          </Box>

          {editing && editingDeparture && (
            <Stack spacing={2}>
              <Typography variant="subtitle1">
                {editingDeparture.startDate}
                {editingDeparture.name ? ` · ${editingDeparture.name}` : ''}
              </Typography>
              <NativeTourItineraryEditor
                value={editing.itinerary}
                onChange={(itinerary) => setEditing({ ...editing, itinerary })}
                lengthInDays={editingDeparture.lengthInDays}
                hosts={hosts}
              />
              <Stack direction="row" spacing={2}>
                <Button
                  variant="contained"
                  disabled={save.isPending || isItineraryInvalid(editing.itinerary, editingDeparture.lengthInDays)}
                  onClick={async () => {
                    try {
                      await saveOne(editing.departureId, editing.itinerary);
                      enqueueSnackbar('Departure itinerary saved', { variant: 'success' });
                      setEditing(null);
                    } catch (saveError) {
                      enqueueSnackbar(`Couldn't save the departure itinerary: ${saveError.message}`, {
                        variant: 'error',
                      });
                    }
                  }}
                >
                  Save departure itinerary
                </Button>
                <Button
                  variant="text"
                  disabled={save.isPending}
                  onClick={async () => {
                    try {
                      await saveOne(editing.departureId, { days: [], transfer: null });
                      enqueueSnackbar("The departure follows the option's itinerary again", { variant: 'success' });
                      setEditing(null);
                    } catch (removeError) {
                      enqueueSnackbar(`Couldn't remove it: ${removeError.message}`, { variant: 'error' });
                    }
                  }}
                >
                  Remove its own itinerary
                </Button>
                <Button variant="text" onClick={() => setEditing(null)}>
                  Cancel
                </Button>
              </Stack>
            </Stack>
          )}
        </Stack>
      )}
    </NativeTourCard>
  );
}
